export type UiLanguage = 'en' | 'ar';

const en = {
  productName: 'AmpShift EMR',
  noActivePatient: 'No active patient',
  setPatientContext: 'Set patient context',
  activeEncounter: 'Encounter',
  mrn: 'MRN',
  age: 'Age',
  dob: 'DOB',
  listening: 'Listening',
  hearing: 'hearing…',
  requestingMic: 'Requesting microphone…',
  connecting: 'Connecting…',
  finalizing: 'Finalizing…',
  voiceError: 'Voice error',
  voiceOutputOn: 'Voice output on',
  voiceOutputOff: 'Voice output off',
  settings: 'Settings',
  signOut: 'Sign out',
  language: 'Language',
  composerPlaceholder: 'Type a request… e.g. "Show the last CBC for my patient"',
  send: 'Send',
  micStart: 'Start listening',
  micStop: 'Stop listening',
  micHold: 'Hold to talk',
  shortcutHint: 'Press {shortcut} to talk',
  shortcutHoldHint: 'Hold {shortcut} to talk',
  escCancels: 'Esc cancels',
  thinking: 'Working…',
  aiNarrative: 'AI narrative',
  aiSummaryLabel: 'AI-generated summary — verify against the record',
  confirm: 'Confirm',
  cancel: 'Cancel',
  acknowledge: 'I acknowledge',
  expiresIn: 'Expires in {seconds}s',
  expired: 'Expired',
  submitted: 'Submitted',
  checkIn: 'Check in',
  startEncounter: 'Start encounter',
  pickSlot: 'Pick a slot',
  missingField: 'Required',
  unknownSurface: 'This response contains a surface this version cannot display.',
  clarificationTranscription: 'I may have misheard.',
  clarificationInterpretation: 'I am not sure what you meant.',
  clarificationAmbiguous: 'More than one match.',
  clarificationConfirmation: 'Please confirm.',
  clarificationMissing: 'I need one more detail.',
  clarificationValidation: 'That did not validate.',
  whichPatient: 'Which patient should be active? Say or type "set my patient context to <name or MRN>".',
  whoIsActive: 'Who is my active patient?',
  clearContext: 'Clear the current patient context',
  micDenied: 'Microphone access was denied. Allow the microphone for this site to use voice input.',
  micUnavailable: 'No microphone is available in this browser.',
  insecureContext: 'Voice input needs a secure context (https or localhost).',
  sessionExpired: 'Your session expired. Please sign in again.',
  signIn: 'Sign in',
  email: 'Email',
  password: 'Password',
  signingIn: 'Signing in…',
  demoPrincipals: 'Demo principals (local development only)',
  loadingPrincipals: 'Loading principals…',
  voiceOutput: 'Voice output',
  arabicVoice: 'Arabic voice',
  englishVoice: 'English voice',
  inputLanguage: 'Voice input language',
  auto: 'Auto-detect',
  listeningMode: 'Listening mode',
  pressToToggle: 'Press to toggle',
  pressAndHold: 'Press and hold',
  toggleShortcut: 'Shortcut',
  pushToTalkShortcut: 'Push-to-talk shortcut',
  pressKeys: 'Press keys…',
  change: 'Change',
  silenceThreshold: 'Silence threshold',
  uiLanguage: 'Interface language',
  appearance: 'Appearance',
  themeSystem: 'Match device',
  themeLight: 'Light',
  themeDark: 'Dark',
  close: 'Close',
  settingsSaved: 'Saved',
  settingsSaveFailed: 'Could not save preferences',
  privacyNote: 'Audio is streamed only while the indicator is visible and is never stored.',
  none: 'None recorded',
  status: 'Status',
  author: 'Author',
  date: 'Date',
  provider: 'Provider',
  patient: 'Patient',
  reset: 'Reset to defaults',
  shortcutCaveat: 'Some browsers or operating systems reserve combos; choose another if it does not fire.',
  stopSpeaking: 'Stop speaking',
  retry: 'Retry',
} as const;

export type StringKey = keyof typeof en;

const ar: Record<StringKey, string> = {
  productName: 'AmpShift EMR',
  noActivePatient: 'لا يوجد مريض نشط',
  setPatientContext: 'تعيين سياق المريض',
  activeEncounter: 'زيارة',
  mrn: 'رقم الملف',
  age: 'العمر',
  dob: 'تاريخ الميلاد',
  listening: 'جارٍ الاستماع',
  hearing: 'أسمع…',
  requestingMic: 'طلب إذن الميكروفون…',
  connecting: 'جارٍ الاتصال…',
  finalizing: 'جارٍ الإنهاء…',
  voiceError: 'خطأ صوتي',
  voiceOutputOn: 'الإخراج الصوتي مفعّل',
  voiceOutputOff: 'الإخراج الصوتي متوقف',
  settings: 'الإعدادات',
  signOut: 'تسجيل الخروج',
  language: 'اللغة',
  composerPlaceholder: 'اكتب طلباً… مثال: "اعرض آخر تحليل دم للمريض"',
  send: 'إرسال',
  micStart: 'ابدأ الاستماع',
  micStop: 'أوقف الاستماع',
  micHold: 'اضغط باستمرار للتحدث',
  shortcutHint: 'اضغط {shortcut} للتحدث',
  shortcutHoldHint: 'اضغط باستمرار {shortcut} للتحدث',
  escCancels: 'Esc للإلغاء',
  thinking: 'جارٍ العمل…',
  aiNarrative: 'سرد بالذكاء الاصطناعي',
  aiSummaryLabel: 'ملخص مُولَّد بالذكاء الاصطناعي — تحقق من السجل',
  confirm: 'تأكيد',
  cancel: 'إلغاء',
  acknowledge: 'أقرّ بذلك',
  expiresIn: 'تنتهي خلال {seconds} ث',
  expired: 'انتهت الصلاحية',
  submitted: 'تم الإرسال',
  checkIn: 'تسجيل الوصول',
  startEncounter: 'بدء الزيارة',
  pickSlot: 'اختر موعداً',
  missingField: 'مطلوب',
  unknownSurface: 'تحتوي هذه الاستجابة على عنصر لا يمكن عرضه في هذا الإصدار.',
  clarificationTranscription: 'ربما لم أسمع بشكل صحيح.',
  clarificationInterpretation: 'لست متأكداً مما قصدت.',
  clarificationAmbiguous: 'أكثر من نتيجة مطابقة.',
  clarificationConfirmation: 'يرجى التأكيد.',
  clarificationMissing: 'أحتاج تفصيلاً إضافياً.',
  clarificationValidation: 'لم يتم التحقق من ذلك.',
  whichPatient: 'أي مريض يجب أن يكون نشطاً؟ قل أو اكتب "اضبط سياق المريض إلى <الاسم أو رقم الملف>".',
  whoIsActive: 'من هو مريضي النشط؟',
  clearContext: 'امسح سياق المريض الحالي',
  micDenied: 'تم رفض الوصول إلى الميكروفون. اسمح باستخدام الميكروفون لهذا الموقع لاستخدام الإدخال الصوتي.',
  micUnavailable: 'لا يوجد ميكروفون متاح في هذا المتصفح.',
  insecureContext: 'يتطلب الإدخال الصوتي سياقاً آمناً (https أو localhost).',
  sessionExpired: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
  signIn: 'تسجيل الدخول',
  email: 'البريد الإلكتروني',
  password: 'كلمة المرور',
  signingIn: 'جارٍ تسجيل الدخول…',
  demoPrincipals: 'حسابات تجريبية (للتطوير المحلي فقط)',
  loadingPrincipals: 'جارٍ تحميل الحسابات…',
  voiceOutput: 'الإخراج الصوتي',
  arabicVoice: 'الصوت العربي',
  englishVoice: 'الصوت الإنجليزي',
  inputLanguage: 'لغة الإدخال الصوتي',
  auto: 'اكتشاف تلقائي',
  listeningMode: 'وضع الاستماع',
  pressToToggle: 'اضغط للتبديل',
  pressAndHold: 'اضغط باستمرار',
  toggleShortcut: 'الاختصار',
  pushToTalkShortcut: 'اختصار الضغط للتحدث',
  pressKeys: 'اضغط المفاتيح…',
  change: 'تغيير',
  silenceThreshold: 'حد الصمت',
  uiLanguage: 'لغة الواجهة',
  appearance: 'المظهر',
  themeSystem: 'حسب الجهاز',
  themeLight: 'فاتح',
  themeDark: 'داكن',
  close: 'إغلاق',
  settingsSaved: 'تم الحفظ',
  settingsSaveFailed: 'تعذر حفظ التفضيلات',
  privacyNote: 'يُبث الصوت فقط أثناء ظهور المؤشر ولا يُخزَّن أبداً.',
  none: 'لا يوجد مسجل',
  status: 'الحالة',
  author: 'الكاتب',
  date: 'التاريخ',
  provider: 'الطبيب',
  patient: 'المريض',
  reset: 'إعادة الضبط إلى الافتراضي',
  shortcutCaveat: 'قد تحجز بعض المتصفحات أو أنظمة التشغيل بعض الاختصارات؛ اختر اختصاراً آخر إذا لم يعمل.',
  stopSpeaking: 'إيقاف النطق',
  retry: 'إعادة المحاولة',
};

const DICTS: Record<UiLanguage, Record<StringKey, string>> = { en, ar };

export function translate(lang: UiLanguage, key: StringKey, params: Record<string, string | number> = {}): string {
  const base = DICTS[lang][key] ?? en[key];
  return Object.entries(params).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), base);
}
